import React, { useEffect } from 'react';
import { Mark, useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { sanitize } from '../../services/renderingService';
import { TipTapToolbar } from './TipTapToolbar';
import './TipTapEditor.css';

const Underline = Mark.create({
  name: 'underline',
  parseHTML() {
    return [{ tag: 'u' }];
  },
  renderHTML() {
    return ['u', 0];
  },
});

interface TipTapEditorProps {
  id?: string;
  value: string;
  onTextChange: (e: { htmlValue: string | null }) => void;
  style?: React.CSSProperties;
  className?: string;
  readOnly?: boolean;
  headerTemplate?: ((editor: any) => React.ReactNode) | React.ReactNode;
}

export const TipTapEditor: React.FC<TipTapEditorProps> = ({
  id,
  value,
  onTextChange,
  style,
  className,
  readOnly = false,
  headerTemplate,
}) => {
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: value || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Sanitize output for security (mitigates XSS vulnerabilities)
      const sanitizedHtml = sanitize(html);
      onTextChange({ htmlValue: sanitizedHtml });
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content',
      },
    },
  });

  // Update editor content when value prop changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  // Update editable state when readOnly changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  if (!editor) {
    return null;
  }

  const renderToolbar = () => {
    if (typeof headerTemplate === 'function') {
      return headerTemplate(editor);
    }
    if (headerTemplate) {
      return headerTemplate;
    }
    return (
      <div className="tiptap-toolbar">
        <TipTapToolbar editor={editor} disabled={readOnly} />
      </div>
    );
  };

  return (
    <div id={id} className={`tiptap-editor-wrapper ${className || ''}`} style={style}>
      {renderToolbar()}
      <EditorContent editor={editor} />
    </div>
  );
};

export default TipTapEditor;
